import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff, PersonAdd } from '@mui/icons-material';
import { ThemeToggle } from '@/components/common';
import { useFormik } from 'formik';
import { loginValidationSchema } from '@/validation';
import { useAuth } from '@/hooks';
import { LoginRequest } from '@/constants/types';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { USER_ROLES } from '@/constants/app';

// Masonry card types and data
interface MasonryCard {
  id: number;
  title: string;
  subtitle?: string;
  color: string;
  size: 'small' | 'medium' | 'large' | 'portrait';
  position: { x: number; y: number };
  image?: string;
  icon?: 'plus' | 'pin';
  shuffleDelay?: number;
}

const masonryData: MasonryCard[] = [
  { id: 1, title: 'Brand &', subtitle: 'Marketing', color: '#ff5722', size: 'medium', position: { x: 2, y: 3 }, icon: 'plus' },
  { id: 2, title: 'Events', subtitle: 'Calendar', color: '#ffffff', size: 'portrait', position: { x: 88, y: 5 }, icon: 'plus', image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&q=80' },
  { id: 3, title: 'Knowledge', subtitle: 'Hub', color: '#e91e63', size: 'large', position: { x: 75, y: 60 }, icon: 'plus' },
  { id: 4, title: 'Human', subtitle: 'Resources', color: '#ffffff', size: 'small', position: { x: 0, y: 35 }, icon: 'pin', image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=400&q=80' },
  { id: 5, title: 'Learning', subtitle: 'Development', color: '#7c4dff', size: 'portrait', position: { x: 5, y: 55 }, icon: 'plus' },
  { id: 6, title: 'Tax Forms', subtitle: 'Management', color: '#ffffff', size: 'medium', position: { x: 82, y: 30 }, icon: 'pin', image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&q=80' },
  { id: 7, title: 'Team', subtitle: 'Collaboration', color: '#4caf50', size: 'small', position: { x: 55, y: 85 }, icon: 'plus' },
  { id: 8, title: 'Finance', subtitle: 'Center', color: '#2196f3', size: 'small', position: { x: 92, y: 75 }, icon: 'plus' },
  { id: 9, title: 'Analytics', subtitle: 'Dashboard', color: '#9c27b0', size: 'medium', position: { x: 20, y: 0 }, icon: 'plus' },
  { id: 10, title: 'Hiring', subtitle: 'Portal', color: '#ffffff', size: 'portrait', position: { x: 0, y: 70 }, icon: 'pin', image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&q=80' },
  { id: 11, title: 'Payroll', subtitle: 'System', color: '#00bcd4', size: 'small', position: { x: 45, y: 2 }, icon: 'plus' },
  { id: 12, title: 'Training', subtitle: 'Academy', color: '#ffffff', size: 'large', position: { x: 65, y: 0 }, icon: 'plus', image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&q=80' },
  { id: 13, title: 'Benefits', subtitle: 'Portal', color: '#ff9800', size: 'small', position: { x: 30, y: 88 }, icon: 'pin' },
  { id: 14, title: 'Performance', subtitle: 'Reviews', color: '#ffffff', size: 'medium', position: { x: 70, y: 85 }, icon: 'plus', image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&q=80' },
  { id: 15, title: 'Onboarding', color: '#3f51b5', size: 'small', position: { x: 0, y: 0 }, icon: 'plus' },
  { id: 16, title: 'Compliance', subtitle: 'Center', color: '#607d8b', size: 'portrait', position: { x: 92, y: 50 }, icon: 'pin' },
];

const animationKeyframes = `
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    25% { transform: translateY(-10px) rotate(1deg); }
    50% { transform: translateY(-5px) rotate(0deg); }
    75% { transform: translateY(-8px) rotate(-1deg); }
  }
  @keyframes floatReverse {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    25% { transform: translateY(8px) rotate(-1deg); }
    50% { transform: translateY(5px) rotate(0deg); }
    75% { transform: translateY(10px) rotate(1deg); }
  }
`;

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const { login, isAuthenticated, user, isLoading, error, clearAuthError } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [shuffledData, setShuffledData] = React.useState(masonryData);

  // Shuffle cards periodically with staggered delays
  React.useEffect(() => {
    const predefinedPositions = {
      small: [
        { x: 0, y: 0 }, { x: 45, y: 2 }, { x: 92, y: 75 }, { x: 55, y: 85 },
        { x: 0, y: 35 }, { x: 30, y: 88 }
      ],
      medium: [
        { x: 2, y: 3 }, { x: 82, y: 30 }, { x: 20, y: 0 }, { x: 70, y: 85 }
      ],
      large: [
        { x: 75, y: 60 }, { x: 65, y: 0 }
      ],
      portrait: [
        { x: 88, y: 5 }, { x: 5, y: 55 }, { x: 0, y: 70 }, { x: 92, y: 50 }
      ]
    };

    const shuffleCards = () => {
      const newShuffledData: MasonryCard[] = [];
      const cardsBySize: { [key: string]: MasonryCard[] } = {
        small: [], medium: [], large: [], portrait: []
      };

      masonryData.forEach(card => {
        cardsBySize[card.size].push(card);
      });

      let delayIndex = 0;
      Object.keys(cardsBySize).forEach(size => {
        const sizeCards = cardsBySize[size];
        const sizePositions = [...predefinedPositions[size as keyof typeof predefinedPositions]];

        // Shuffle positions
        for (let i = sizePositions.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [sizePositions[i], sizePositions[j]] = [sizePositions[j], sizePositions[i]];
        }

        sizeCards.forEach((card, index) => {
          newShuffledData.push({
            ...card,
            position: sizePositions[index] || card.position,
            shuffleDelay: delayIndex * 0.1, // Staggered delay
          });
          delayIndex++;
        });
      });

      // Apply cards one by one with delays
      newShuffledData.forEach((card) => {
        setTimeout(() => {
          setShuffledData(prev =>
            prev.map(prevCard =>
              prevCard.id === card.id ? card : prevCard
            )
          );
        }, (card.shuffleDelay || 0) * 1000);
      });
    };

    const interval = setInterval(shuffleCards, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      const params = new URLSearchParams(window.location.search);
      const returnUrl = params.get('returnUrl');
      navigate(returnUrl || getNavigationPath(), { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearAuthError();
    }
  }, [error, clearAuthError]);

  const getNavigationPath = (): string => {
    if (!user) return '/talky';
    
    switch (user.role) {
      case USER_ROLES.SUPER_ADMIN:
        return '/talky';
      case USER_ROLES.ADMINISTRATOR:
        return '/talky';
      case USER_ROLES.STAFF:
        return '/talky';
      case USER_ROLES.USER:
        return '/talky';
      case USER_ROLES.PARENTS:
        return '/talky';
      default:
        return '/talky';
    }
  };

  const formik = useFormik<LoginRequest>({
    initialValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
    validationSchema: loginValidationSchema,
    onSubmit: async (values) => {
      try {
        const params = new URLSearchParams(window.location.search);
        const returnUrl = params.get('returnUrl');
        await login(values);
        toast.success('Login successful!');
        navigate(returnUrl || getNavigationPath(), { replace: true });
      } catch (error: any) {
        console.error('Login error:', error);
      }
    },
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getSizeStyles = (size: string) => {
    switch (size) {
      case 'small': return { width: 100, height: 100 };
      case 'medium': return { width: 140, height: 110 };
      case 'large': return { width: 130, height: 160 };
      case 'portrait': return { width: 110, height: 140 };
      default: return { width: 140, height: 110 };
    }
  };

  const masonryCards = useMemo(() => {
    return shuffledData.map((card, index) => (
      <Box
        key={card.id}
        sx={{
          position: 'absolute',
          left: `${card.position.x}%`,
          top: `${card.position.y}%`,
          ...getSizeStyles(card.size),
          backgroundColor: card.image ? 'transparent' : card.color,
          backgroundImage: card.image ? `url(${card.image})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'flex-start',
          padding: 1.5,
          boxShadow: theme.palette.mode === 'light'
            ? '0 4px 20px rgba(0,0,0,0.15)'
            : '0 4px 20px rgba(0,0,0,0.3)',
          opacity: 0.7,
          animation: index % 2 === 0
            ? `float ${8 + (index * 0.3)}s ease-in-out infinite`
            : `floatReverse ${8 + (index * 0.3)}s ease-in-out infinite`,
          animationDelay: `${index * 0.2}s`,
          transition: 'left 1.2s cubic-bezier(0.4, 0, 0.2, 1), top 1.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s ease, opacity 0.3s ease, box-shadow 0.3s ease',
          overflow: 'hidden',
          '&::before': card.image ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.1) 100%)',
            zIndex: 1,
          } : {},
          '&:hover': {
            opacity: 1,
            transform: 'scale(1.05)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
          },
        }}
      >
        <Typography
          sx={{
            position: 'relative',
            zIndex: 2,
            fontSize: card.size === 'large' ? '0.9rem' : '0.75rem',
            fontWeight: 700,
            color: card.image || card.color !== '#ffffff' ? 'white' : '#333',
            lineHeight: 1.2,
            textShadow: card.image ? '0 1px 3px rgba(0,0,0,0.5)' : 'none',
          }}
        >
          {card.title}
        </Typography>
        {card.subtitle && (
          <Typography
            sx={{
              position: 'relative',
              zIndex: 2,
              fontSize: '0.65rem',
              color: card.image || card.color !== '#ffffff' ? 'rgba(255,255,255,0.9)' : '#666',
              lineHeight: 1.2,
              textShadow: card.image ? '0 1px 2px rgba(0,0,0,0.5)' : 'none',
            }}
          >
            {card.subtitle}
          </Typography>
        )}
      </Box>
    ));
  }, [theme.palette.mode, shuffledData]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: animationKeyframes }} />
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, #e8f4f8 0%, #f5f5f5 50%, #e8eef8 100%)'
            : 'linear-gradient(135deg, #1a1a2e 0%, #121212 50%, #1a1a2e 100%)',
          p: { xs: 2, sm: 4 },
          overflow: 'hidden',
        }}
      >
        {/* Animated Masonry Background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          {masonryCards}
        </Box>

        <Card
          elevation={0}
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            width: '100%',
            maxWidth: 900,
            minHeight: { xs: 'auto', md: 540 },
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: theme.palette.mode === 'light'
              ? '0 8px 40px rgba(0,0,0,0.12)'
              : '0 8px 40px rgba(0,0,0,0.5)',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === 'light' ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
        {/* Left Side - Branding */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '45%',
            background: 'linear-gradient(135deg, #0a66c2 0%, #004182 100%)',
            p: 5,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />

          {/* Content */}
          <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', width: '100%' }}>
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 2,
                lineHeight: 1.3,
              }}
            >
              HRTalky – India's HR Learning, Jobs & Networking Platform
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.85)',
                maxWidth: 300,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Securely log in to access HR courses, job boards, career opportunities, community networking, and expert HR resources. Join India's trusted HR platform today.
            </Typography>

            {/* Features */}
            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
             {['Connect with recruiters', 'Find your dream job', 'Build your network'].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      color: 'white',
                    }}
                  >
                    ✓
                  </Box>
                  <Typography variant="body2" sx={{ color: 'white' }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Right Side - Login Form */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: { xs: 3, sm: 5 },
            bgcolor: theme.palette.mode === 'light' ? '#ffffff' : '#1e1e1e',
          }}
        >
          {/* Theme Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <ThemeToggle />
          </Box>

          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 3, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img
              src={theme.palette.mode === 'light' ? '/logoNew.png' : '/white.png'}
              alt="Logo"
              style={{ maxWidth: 140, maxHeight: 48, objectFit: 'contain' }}
            />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Sign In
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your credentials to access your account
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              required
              name="email"
              label="Email Address"
              type="email"
              value={formik.values.email}
              onChange={(e) => {
                const lowerCaseEmail = e.target.value?.trim()?.toLowerCase();
                formik.setFieldValue('email', lowerCaseEmail);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              sx={{ mb: 2 }}
              autoComplete="username"
            />

            <TextField
              fullWidth
              required
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              autoComplete="current-password"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        aria-label="toggle password visibility"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            {/* Auth Links */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, mb: 2 }}>
              <Button
                component={Link}
                to="/forgot-password"
                variant="text"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  color: 'text.secondary',
                  p: 0,
                  '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                }}
              >
                Forgot password?
              </Button>
              <Button
                component={Link}
                to="/resend-verification"
                variant="text"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  color: 'text.secondary',
                  p: 0,
                  '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                }}
              >
                Resend verification
              </Button>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                mt: 1,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                bgcolor: '#0a66c2',
                '&:hover': { bgcolor: '#004182' },
              }}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          {/* Divider */}
          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary">
              OR
            </Typography>
          </Divider>

          {/* Register Link */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Don't have an account?
            </Typography>
            <Button
              component={Link}
              to="/register"
              variant="outlined"
              fullWidth
              startIcon={<PersonAdd />}
              sx={{
                py: 1.25,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: theme.palette.divider,
                color: 'text.primary',
                '&:hover': {
                  borderColor: '#0a66c2',
                  bgcolor: 'rgba(10, 102, 194, 0.04)',
                },
              }}
            >
              Create Account
            </Button>
          </Box>
        </Box>
        </Card>
      </Box>
    </>
  );
};